// src/pages/GameDetails.js
import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import games from "../data/gamesData";
import "./GameDetails.css";
import useIsMobile from "../hooks/useIsMobile";
import { useTranslation } from "react-i18next";
import Stars from "../components/Stars";
import SmartDownloadButton from "../components/SmartDownloadButton";
import PageWrapper from "../components/PageWrapper";
import Footer from "../components/Footer";
import { logEvent } from "../firebase";
import { getStoreUrlForDevice, shouldAutoOpenStore } from "../utils/storeRedirect";
import { setPageMeta, upscaleOgImage } from "../utils/pageMeta";
import {
  faqJsonLd,
  resolveLanding,
  softwareJsonLd,
} from "../utils/appSeo";
import FAQItem from "../components/FAQItem";

function upscaleScreenshot(url) {
  return url.replace(/\/\d+x\d+bb\./, "/600x1200bb.");
}

function formatFileSize(bytes) {
  if (!bytes) return null;
  const mb = Number(bytes) / (1024 * 1024);
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${Math.round(mb)} MB`;
}

function formatReleaseDate(iso, locale) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function InfoItem({ label, value }) {
  if (!value) return null;
  return (
    <div className="info-item">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

export default function GameDetails() {
  const { id } = useParams();
  const game = games.find((g) => g.id === id);
  const appStoreId = game?.apple_id;
  const countryCode = game?.countryCode || "us";
  const { t, i18n } = useTranslation();
  const [screenshots, setScreenshots] = useState([]);
  const [ageRating, setAgeRating] = useState("");
  const [description, setDescription] = useState("");
  const [rating, setRating] = useState(null);
  const [ratingCount, setRatingCount] = useState(null);
  const [genre, setGenre] = useState("");
  const [version, setVersion] = useState("");
  const [fileSize, setFileSize] = useState("");
  const [price, setPrice] = useState("");
  const [developer, setDeveloper] = useState("");
  const [releaseDate, setReleaseDate] = useState("");
  const [storeTitle, setStoreTitle] = useState("");
  const [loading, setLoading] = useState(Boolean(appStoreId));
  const [current, setCurrent] = useState(0);
  const isMobile = useIsMobile();
  const [expanded, setExpanded] = useState(false);
  const carouselRef = useRef();

  const landing = useMemo(
    () => (game ? resolveLanding(game) : null),
    [game]
  );
  const pageUrl = game ? `https://mindblows.com/apps/${game.id}` : "";

  useEffect(() => {
    if (!game || !landing) return;

    setPageMeta({
      title: landing.seoTitle,
      description: landing.seoDescription,
      image: upscaleOgImage(game.icon),
      url: pageUrl,
      keywords: landing.keywords,
      robots: "index, follow",
      jsonLd: [
        softwareJsonLd(game, landing, pageUrl),
        faqJsonLd(landing.faq),
      ],
    });
  }, [game, landing, pageUrl]);

  useEffect(() => {
    if (!game) return;
    const storeUrl = getStoreUrlForDevice(game.appStoreUrl, game.playStoreUrl);
    if (!storeUrl || !shouldAutoOpenStore()) return;

    logEvent("app_page_store_redirect", {
      app: game.shortTitle || game.title,
      via: "share",
    });
    window.location.replace(storeUrl);
  }, [game]);

  const scrollToIndex = (index) => {
    if (!carouselRef.current) return;
    const el = carouselRef.current;
    const clamped = (index + screenshots.length) % screenshots.length;
    el.scrollTo({ left: el.clientWidth * clamped, behavior: "smooth" });
    setCurrent(clamped);
  };

  useEffect(() => {
    setExpanded(false);
    setCurrent(0);

    if (!appStoreId) {
      setScreenshots([]);
      setDescription("");
      setRating(null);
      setRatingCount(null);
      setAgeRating("");
      setGenre("");
      setVersion("");
      setFileSize("");
      setPrice("");
      setDeveloper("");
      setReleaseDate("");
      setStoreTitle("");
      setLoading(false);
      return;
    }

    setLoading(true);
    const controller = new AbortController();
    const lookupUrl = `https://itunes.apple.com/lookup?id=${appStoreId}&country=${countryCode}`;

    fetch(lookupUrl, { signal: controller.signal })
      .then((res) => res.json())
      .then((data) => {
        const result = data.results?.[0];
        if (!result) return;

        const shots = [
          ...(result.screenshotUrls || []),
          ...(result.ipadScreenshotUrls || []),
        ].map(upscaleScreenshot);

        setScreenshots(shots);
        if (!landing?.description) {
          setDescription(result.description || "");
        }
        setRating(result.averageUserRating ?? null);
        setRatingCount(result.userRatingCount ?? 0);
        setAgeRating(result.trackContentRating || "");
        setGenre(result.primaryGenreName || "");
        setVersion(result.version || "");
        setFileSize(formatFileSize(result.fileSizeBytes));
        setPrice(result.formattedPrice || t("app_free"));
        setDeveloper(result.sellerName || "");
        setReleaseDate(
          formatReleaseDate(result.currentVersionReleaseDate, i18n.language)
        );
        setStoreTitle(result.trackName || "");
      })
      .catch(() => {})
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [appStoreId, countryCode, i18n.language, t, landing?.description]);

  if (!game || !landing) {
    return (
      <h2 style={{ paddingTop: 100, textAlign: "center" }}>App not found</h2>
    );
  }

  const hasRatings = rating != null && ratingCount > 0;
  const infoItems = [
    { label: t("app_category"), value: genre || t(game.categoryKey) },
    { label: t("app_version"), value: version },
    { label: t("app_size"), value: fileSize },
    { label: t("app_price"), value: price },
    { label: t("app_developer"), value: developer },
    { label: t("app_updated"), value: releaseDate },
  ].filter((item) => item.value);

  return (
    <>
      <PageWrapper>
        <div className="game-details">
          <Link to="/apps" className="back-link">
            ← {t("back_to_apps")}
          </Link>

          <section className="app-hero">
            <img
              src={game.icon}
              alt={landing.seoTitle}
              className="details-icon"
            />
            <div className="details-text">
              <span className="app-category-pill">{t(game.categoryKey)}</span>
              <h1>{landing.seoTitle}</h1>
              {game.shortTitle &&
                landing.seoTitle !== game.shortTitle &&
                (storeTitle || game.title) !== landing.seoTitle && (
                  <p className="app-full-title">
                    {storeTitle || game.title}
                  </p>
                )}
              {landing.seoDescription && (
                <p className="app-seo-lede">{landing.seoDescription}</p>
              )}

              <div className="app-meta-row">
                {hasRatings ? (
                  <div className="app-rating">
                    <Stars rating={rating} />
                    <span className="rating-number">
                      {rating.toFixed(1)} · {ratingCount.toLocaleString()}{" "}
                      {t("app_ratings")}
                    </span>
                  </div>
                ) : appStoreId ? (
                  <span className="app-badge app-badge-new">
                    {t("app_new")}
                  </span>
                ) : null}
                {ageRating && (
                  <span
                    className={`age-rating ${
                      ageRating === "17+" ? "age-17" : ""
                    }`}
                  >
                    {ageRating}
                  </span>
                )}
                {game.downloads && (
                  <span className="app-badge">{game.downloads}</span>
                )}
              </div>

              <div className="store-availability">
                {game.appStoreUrl && (
                  <span className="store-chip">App Store</span>
                )}
                {game.playStoreUrl && (
                  <span className="store-chip">Google Play</span>
                )}
              </div>

              <SmartDownloadButton
                className="download-button"
                appStoreUrl={game.appStoreUrl}
                playStoreUrl={game.playStoreUrl}
                gameName={game.shortTitle}
              />
            </div>
          </section>

          {(loading || screenshots.length > 0) && (
            <section className="app-section">
              <h2>{t("app_preview")}</h2>
              {loading ? (
                <div className="screenshots-skeleton">
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="skeleton-shot" />
                  ))}
                </div>
              ) : (
                <div
                  dir="ltr"
                  className={`carousel-wrapper ${
                    isMobile ? "mobile" : "desktop"
                  }`}
                >
                  {isMobile ? (
                    <>
                      <div
                        className="carousel-track swipeable"
                        ref={carouselRef}
                      >
                        {screenshots.map((src, i) => (
                          <div key={i} className="screenshot-frame">
                            <img
                              src={src}
                              alt={`${game.shortTitle} screenshot ${i + 1}`}
                              className="carousel-slide"
                            />
                          </div>
                        ))}
                      </div>
                      {screenshots.length > 1 && (
                        <div className="carousel-nav">
                          <button
                            type="button"
                            onClick={() => scrollToIndex(current - 1)}
                            aria-label={t("a11y_prev_screenshot")}
                          >
                            ‹
                          </button>
                          <button
                            type="button"
                            onClick={() => scrollToIndex(current + 1)}
                            aria-label={t("a11y_next_screenshot")}
                          >
                            ›
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="carousel-scroll">
                      {screenshots.map((src, i) => (
                        <div key={i} className="screenshot-frame">
                          <img
                            src={src}
                            alt={`${game.shortTitle} screenshot ${i + 1}`}
                            className="scroll-slide"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </section>
          )}

          {landing.features.length > 0 && (
            <section className="app-section">
              <h2>{t("app_features")}</h2>
              <ul className="app-feature-list">
                {landing.features.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </section>
          )}

          {(landing.description || description) && (
            <section className="app-section">
              <h2>{t("app_about")}</h2>
              <p className={`app-description ${expanded ? "expanded" : ""}`}>
                {landing.description || description}
              </p>
              {(landing.description || description).split("\n").length > 8 && (
                <button
                  type="button"
                  onClick={() => setExpanded(!expanded)}
                  className="show-more-btn"
                >
                  {expanded ? t("show_less") : t("show_more")}
                </button>
              )}
            </section>
          )}

          {landing.faq.length > 0 && (
            <section className="app-section app-faq">
              <h2>{t("app_faq")}</h2>
              {landing.faq.map((item) => (
                <FAQItem key={item.q} question={item.q} answer={item.a} />
              ))}
            </section>
          )}

          {infoItems.length > 0 && (
            <section className="app-section">
              <h2>{t("app_information")}</h2>
              <dl className="info-grid">
                {infoItems.map((item) => (
                  <InfoItem
                    key={item.label}
                    label={item.label}
                    value={item.value}
                  />
                ))}
              </dl>
            </section>
          )}

          {game.video && (
            <section className="app-section">
              <h2>{t("app_trailer")}</h2>
              <div className="video">
                <div className="video-wrapper">
                  <iframe
                    src={game.video}
                    title="Trailer"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
            </section>
          )}

          <section className="more-games">
            <h2>{t("more_apps")}</h2>
            <div className="more-games-scroll">
              {games
                .filter((g) => g.id !== game.id)
                .slice(0, 12)
                .map((g) => (
                  <Link
                    to={`/apps/${g.id}`}
                    key={g.id}
                    className="more-game-card"
                  >
                    <img src={g.icon} alt={g.title} />
                    <div>{g.shortTitle}</div>
                  </Link>
                ))}
            </div>
          </section>
        </div>

        <div className="sticky-toolbar">
          <div className="toolbar-buttons">
            {game.appStoreUrl && (
              <a
                href={game.appStoreUrl}
                target="_blank"
                rel="noreferrer"
                onClick={() =>
                  logEvent("store_banner_clicked", {
                    store: "App Store",
                    game: game.shortTitle || game.title,
                  })
                }
              >
                <img
                  src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                  alt="App Store"
                  className="toolbar-banner"
                />
              </a>
            )}
            {game.playStoreUrl && (
              <a
                href={game.playStoreUrl}
                target="_blank"
                rel="noreferrer"
                onClick={() =>
                  logEvent("store_banner_clicked", {
                    store: "Google Play",
                    game: game.shortTitle || game.title,
                  })
                }
              >
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                  alt="Google Play"
                  className="toolbar-banner"
                />
              </a>
            )}
          </div>
        </div>
      </PageWrapper>
      <Footer />
    </>
  );
}
